import type { InterventionStatus } from '@garage/shared';
import type { ReactNode } from 'react';
import { STATUS_LABELS } from '../garage/lib/format.js';
import { cx } from './cx.js';

/**
 * Pastilles d'information. Le texte porte toujours le sens ; la couleur ne fait que le souligner.
 * Tons : neutre, marque, sémantiques, auteurs du journal.
 */
export type BadgeTone =
  | 'neutral'
  | 'outline'
  | 'brand'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info'
  | 'author-garage'
  | 'author-customer'
  | 'author-system'
  | 'author-support';

const TONE: Record<BadgeTone, string> = {
  neutral: 'bg-sunken text-ink-2',
  outline: 'bg-surface text-ink-2 ring-1 ring-inset ring-line-strong',
  brand: 'bg-brand text-on-brand',
  success: 'bg-success-bg text-success-fg',
  warning: 'bg-warning-bg text-warning-fg',
  danger: 'bg-danger-bg text-danger-fg',
  info: 'bg-info-bg text-info-fg',
  'author-garage': 'bg-author-garage-bg text-author-garage-fg',
  'author-customer': 'bg-author-customer-bg text-author-customer-fg',
  'author-system': 'bg-author-system-bg text-author-system-fg',
  'author-support': 'bg-author-support-bg text-author-support-fg',
};

const DOT: Partial<Record<BadgeTone, string>> = {
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  info: 'bg-info',
  'author-garage': 'bg-author-garage',
  'author-customer': 'bg-author-customer',
  'author-system': 'bg-author-system',
  'author-support': 'bg-author-support',
};

export function Badge({
  tone = 'neutral',
  dot = false,
  icon,
  className,
  children,
}: {
  tone?: BadgeTone;
  /** Petite pastille de couleur avant le texte. */
  dot?: boolean;
  icon?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <span
      className={cx(
        'inline-flex max-w-full items-center gap-1.5 rounded-full px-2 py-px text-xs leading-5 font-medium whitespace-nowrap',
        TONE[tone],
        className,
      )}
    >
      {dot ? (
        <span aria-hidden className={cx('size-1.5 shrink-0 rounded-full', DOT[tone] ?? 'bg-current')} />
      ) : null}
      {icon}
      <span className="truncate">{children}</span>
    </span>
  );
}

/** Compteur (onglets, menu) : pastille ronde de la couleur de marque. */
export function CountBadge({
  count,
  label,
  className,
}: {
  count: number;
  label?: string;
  className?: string;
}) {
  return (
    <span
      className={cx(
        'inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1.5 text-2xs font-semibold text-on-brand tabular-nums',
        className,
      )}
      aria-label={label}
    >
      {count}
    </span>
  );
}

const STATUS: Record<InterventionStatus, { box: string; dot: string; rail: string; text: string }> = {
  received: {
    box: 'bg-status-received-bg text-status-received-fg',
    dot: 'bg-status-received-dot',
    rail: 'before:bg-status-received-dot',
    text: 'text-status-received-fg',
  },
  in_progress: {
    box: 'bg-status-in-progress-bg text-status-in-progress-fg',
    dot: 'bg-status-in-progress-dot',
    rail: 'before:bg-status-in-progress-dot',
    text: 'text-status-in-progress-fg',
  },
  awaiting_decision: {
    box: 'bg-status-awaiting-decision-bg text-status-awaiting-decision-fg',
    dot: 'bg-status-awaiting-decision-dot',
    rail: 'before:bg-status-awaiting-decision-dot',
    text: 'text-status-awaiting-decision-fg',
  },
  awaiting_parts: {
    box: 'bg-status-awaiting-parts-bg text-status-awaiting-parts-fg',
    dot: 'bg-status-awaiting-parts-dot',
    rail: 'before:bg-status-awaiting-parts-dot',
    text: 'text-status-awaiting-parts-fg',
  },
  ready: {
    box: 'bg-status-ready-bg text-status-ready-fg',
    dot: 'bg-status-ready-dot',
    rail: 'before:bg-status-ready-dot',
    text: 'text-status-ready-fg',
  },
  closed: {
    box: 'bg-status-closed-bg text-status-closed-fg',
    dot: 'bg-status-closed-dot',
    rail: 'before:bg-status-closed-dot',
    text: 'text-status-closed-fg',
  },
};

/**
 * Statut d'intervention : couleurs propres à chaque statut, indépendantes de la marque.
 * `plain` (vague 7) : pastille de couleur et texte coloré, sans fond, pour les listes denses (la
 * bordure de statut de la carte porte déjà la couleur).
 */
export function StatusBadge({
  status,
  label,
  className,
  plain = false,
}: {
  status: InterventionStatus;
  label?: string;
  className?: string;
  plain?: boolean;
}) {
  const s = STATUS[status];
  return (
    <span
      className={cx(
        'inline-flex shrink-0 items-center gap-1.5 text-xs leading-5 font-medium whitespace-nowrap',
        plain ? s.text : cx('rounded-full px-2 py-px', s.box),
        className,
      )}
    >
      <span aria-hidden className={cx('size-1.5 shrink-0 rounded-full', s.dot)} />
      {label ?? STATUS_LABELS[status]}
    </span>
  );
}

/** Classe de la pastille de couleur d'un statut (légendes, étapes). */
export function statusDotClass(status: InterventionStatus): string {
  return STATUS[status].dot;
}

/**
 * Bordure de statut d'une carte (vague 7) : filet de 3 px à la couleur du statut, à gauche, dessiné
 * dans le rayon de la carte (`::before`). La carte doit être `relative overflow-hidden`.
 */
export function statusRailClass(status: InterventionStatus): string {
  return cx("before:pointer-events-none before:absolute before:inset-y-0 before:left-0 before:w-[3px] before:content-['']", STATUS[status].rail);
}

const DAY = 86_400_000;

/** Jours calendaires (heure locale) depuis une date : 0 le jour même. */
export function daysSince(iso: string, now: Date = new Date()): number {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 0;
  const start = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  return Math.max(0, Math.round((start(now) - start(d)) / DAY));
}

/**
 * Pastille « N j » : jours à l'atelier depuis l'entrée (vague 7). Information neutre ; le libellé
 * accessible dit la phrase entière. « Auj. » le jour de l'entrée.
 */
export function DaysBadge({ since, now, className }: { since: string; now?: Date; className?: string }) {
  const n = daysSince(since, now);
  const label = n === 0 ? 'Entré aujourd’hui' : `À l’atelier depuis ${n} jour${n > 1 ? 's' : ''}`;
  return (
    <span
      title={label}
      className={cx(
        'inline-flex h-6 shrink-0 items-center rounded-full bg-sunken px-2 text-xs font-medium text-ink-2 tabular-nums',
        className,
      )}
    >
      <span aria-hidden>{n === 0 ? 'Auj.' : `${n} j`}</span>
      <span className="sr-only">{label}</span>
    </span>
  );
}
